/**
 * Phase 640 — Public parent share page.
 * No auth required — access is gated by the share token in the URL.
 * Parent watches the video with annotations rendered as timestamped
 * overlays. Read-only.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Users, Video, AlertTriangle } from 'lucide-react';
import VideoAnnotationStudio from '@/components/video-analysis/VideoAnnotationStudio';

export default function PublicVideoAnalysisPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    (async () => {
      try {
        const r = await axios.get(`${base}/api/public/video-analyses/${token}`);
        setData(r.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'This link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <p className="text-sm text-muted-foreground">Loading video…</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
         data-testid="public-video-error">
      <Card className="max-w-md w-full border-red-200">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="font-medium">{error}</p>
          <p className="text-xs text-muted-foreground mt-2">
            If your coach shared this with you recently, ask them to send it again.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  if (!data) return null;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const streamUrl = `${base}/api/public/video-analyses/${token}/stream`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900"
         data-testid="public-video-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 text-emerald-600 text-xs font-medium uppercase tracking-wider mb-2">
            <Sparkles className="h-3.5 w-3.5" /> Video from your coach
          </div>
          <h1 className="text-2xl font-display font-semibold" data-testid="public-video-title">
            {data.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5 justify-center">
            <Video className="h-3.5 w-3.5" /> {data.coach_name}
            <span className="text-slate-300">·</span>
            <Users className="h-3.5 w-3.5" /> {data.student_name}
          </p>
        </div>

        {/* Video with annotations (read-only) */}
        <Card>
          <CardContent className="p-3 sm:p-4">
            <VideoAnnotationStudio
              videoSrc={streamUrl}
              annotations={data.annotations || []}
              readonly
            />
          </CardContent>
        </Card>

        {/* Coach's note */}
        {data.comment && (
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Note from {data.coach_name}
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{data.comment}</p>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[10px] text-slate-400">
          Powered by Out-Play. This is a private link — please don&apos;t share it publicly.
        </p>
      </div>
    </div>
  );
}
